import { useState, useEffect } from 'react';
import axios from 'axios';
import { Settings, Key, Webhook, Copy, Check, TestTube } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SettingsPage = () => {
  const [user, setUser] = useState(null);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookEnabled, setWebhookEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
      setWebhookUrl(response.data.webhookUrl || '');
      setWebhookEnabled(response.data.webhookEnabled || false);
    } catch (error) {
      toast.error('Gagal memuat data user');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveWebhook = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/settings/webhook`,
        { webhookUrl, webhookEnabled },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Webhook settings berhasil disimpan');
      fetchUserData();
    } catch (error) {
      toast.error('Gagal menyimpan webhook settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestWebhook = async () => {
    setTesting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/settings/webhook/test`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Webhook test berhasil! Status: ${response.data.status}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Webhook test gagal');
    } finally {
      setTesting(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Disalin ke clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-4xl md:text-5xl font-bold text-gray-800">Settings</h1>
        <p className="text-gray-600 mt-2">Kelola API key dan webhook Anda</p>
      </div>

      {/* User Info */}
      <Card className="glass-effect">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="text-green-600" size={24} />
            Informasi Akun
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Nama</Label>
            <Input value={user?.name || ''} disabled className="bg-gray-50" />
          </div>
          <div>
            <Label>Email</Label>
            <Input value={user?.email || ''} disabled className="bg-gray-50" />
          </div>
        </CardContent>
      </Card>

      {/* API Key */}
      <Card className="glass-effect">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="text-green-600" size={24} />
            API Key
          </CardTitle>
          <CardDescription>
            Gunakan API key ini untuk mengakses API send message
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <Input
              data-testid="api-key-input"
              value={user?.apiKey || ''}
              disabled
              className="bg-gray-50 font-mono text-sm"
            />
            <Button
              data-testid="copy-api-key"
              onClick={() => copyToClipboard(user?.apiKey || '')}
              variant="outline"
            >
              {copied ? <Check size={18} /> : <Copy size={18} />}
            </Button>
          </div>
          <p className="text-sm text-gray-500 mt-2">
            Jangan bagikan API key Anda kepada siapa pun
          </p>
        </CardContent>
      </Card>

      {/* Webhook Settings */}
      <Card className="glass-effect">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Webhook className="text-green-600" size={24} />
            Webhook Settings
          </CardTitle>
          <CardDescription>
            Terima notifikasi pesan masuk melalui webhook
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="webhook-url">Webhook URL</Label>
            <Input
              id="webhook-url"
              data-testid="webhook-url-input"
              type="url"
              placeholder="https://your-domain.com/webhook"
              value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
            />
            <p className="text-sm text-gray-500">
              URL endpoint yang akan menerima POST request saat ada pesan masuk
            </p>
          </div>

          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div>
              <Label htmlFor="webhook-enabled" className="font-semibold">
                Enable Webhook
              </Label>
              <p className="text-sm text-gray-500">Aktifkan notifikasi webhook</p>
            </div>
            <Switch
              id="webhook-enabled"
              data-testid="webhook-enabled-switch"
              checked={webhookEnabled}
              onCheckedChange={setWebhookEnabled}
            />
          </div>

          <div className="flex gap-2">
            <Button
              data-testid="save-webhook-button"
              onClick={handleSaveWebhook}
              disabled={saving}
              className="bg-green-600 hover:bg-green-700 flex-1"
            >
              {saving ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
              ) : (
                'Simpan'
              )}
            </Button>
            <Button
              data-testid="test-webhook-button"
              onClick={handleTestWebhook}
              disabled={testing || !webhookUrl}
              variant="outline"
            >
              {testing ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-green-600" />
              ) : (
                <>
                  <TestTube size={18} className="mr-2" />
                  Test
                </>
              )}
            </Button>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
            <h4 className="font-semibold text-blue-900 mb-2">Format Webhook Payload:</h4>
            <pre className="text-xs bg-white p-3 rounded border overflow-x-auto">
{`{
  "type": "message_received",
  "data": {
    "id": "msg_id",
    "content": "Hello",
    "fromNumber": "628xxx",
    "fromName": "John",
    "mediaType": "text",
    "timestamp": "2024-01-01T00:00:00Z"
  }
}`}
            </pre>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SettingsPage;
